import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_PRODUCT_MODEL_LISTS, type ProductModelLists } from "@/lib/productModelDefaults";
import { DEFAULT_SKIN_PHOTOS, type SkinPhotoLibrary } from "@/lib/skinPhotoLibrary";
import { DEFAULT_HERO_SLIDES, type HeroSlideItem } from "@/lib/heroSlideDefaults";

export interface BrandingSettings {
  siteName: string;
  logoText1: string;
  logoText2: string;
  tagline: string;
}

export interface Testimonial {
  name: string;
  text: string;
  rating: number;
}

export interface HomepageSettings {
  whoAreWeText: string;
  announcementBar: string;
  announcementBarEnabled: boolean;
  quizEnabled: boolean;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface CategoryModel {
  slug: string;
  name: string;
}

export interface CategoryBrand {
  slug: string;
  name: string;
  query: string;
  models: CategoryModel[];
  hidden?: boolean;
}

export interface CategoryItem {
  slug: string;
  labelKey: string;
  productType: string;
  icon: string;
  brands: CategoryBrand[];
  hidden?: boolean;
}

export interface AnnouncementItem {
  text: string;
  enabled: boolean;
}

export interface HeroSettings {
  titleLine1: string;
  titleLine2: string;
  description: string;
  launchDate: string; // ISO string
  showCountdown: boolean;
  ctaPrimaryText: string;
  ctaPrimaryLink: string;
  ctaSecondaryText: string;
  ctaSecondaryLink: string;
  stats: { value: string; label: string }[];
  features: { title: string; description: string; icon: string }[];
  storyParagraphs: string[];
}

export interface FooterSettings {
  copyrightText: string;
  socialLinks: { platform: string; url: string; enabled: boolean }[];
  showNewsletter: boolean;
  newsletterTitle: string;
  newsletterDescription: string;
}

export interface ThemeSettings {
  primaryHue: number;
  primarySaturation: number;
  primaryLightness: number;
  accentHue: number;
  accentSaturation: number;
  accentLightness: number;
  borderRadius: number;
  fontFamily: string;
  headingFont: string;
  fontScale: number;
}

export interface AdminGuideSection {
  id: string;
  title: string;
  icon: string; // lucide icon name
  summary: string;
  steps: string[];
  tips: string;
}

export interface AdminGuideSettings {
  title: string;
  intro: string;
  supportNote: string;
  previewRoutes: { label: string; path: string }[];
  sections: AdminGuideSection[];
}

export interface SeoSettings {
  pageTitle: string;
  metaDescription: string;
  ogImage: string;
  favicon: string;
  googleAnalyticsId: string;
}

export interface ShippingSettings {
  title: string;
  sections: { heading: string; content: string }[];
}

export interface TutorialItem {
  id: string;
  label: string;
  description: string;
  icon: string; // lucide icon name
  youtubeId: string;
  duration: string;
  difficulty: "Easy" | "Medium" | "Advanced";
  preview: string; // image URL (optional, falls back to default)
}

export interface TutorialDeviceTab {
  id: string;
  label: string;
  icon: string; // lucide icon name
  enabled: boolean;
  tutorials: TutorialItem[];
}

export interface TutorialSettings {
  enabled: boolean;
  badge: string;
  title1: string;
  title2: string;
  intro: string;
  helpTitle: string;
  helpDesc: string;
  contactEmail: string;
  contactCtaText: string;
  watchCtaText: string;
  tabs: TutorialDeviceTab[];
}

export interface ConfiguratorExtraModel {
  id?: string;
  name: string;
  category: "phones" | "macbooks" | "cameras" | "controllers" | "consoles";
  basePrice: number;
}

export interface ConfiguratorExtras {
  models: ConfiguratorExtraModel[];
}

export type { ProductModelLists, ProductModelGroup } from "@/lib/productModelDefaults";
export type { HeroSlideItem } from "@/lib/heroSlideDefaults";

export interface SiteSettings {
  branding: BrandingSettings;
  testimonials: Testimonial[];
  homepage: HomepageSettings;
  faq: FaqItem[];
  categories: CategoryItem[];
  announcements: AnnouncementItem[];
  hero: HeroSettings;
  heroSlides: HeroSlideItem[];
  footer: FooterSettings;
  theme: ThemeSettings;
  seo: SeoSettings;
  shipping: ShippingSettings;
  tutorial: TutorialSettings;
  configuratorExtras: ConfiguratorExtras;
  productModelLists: ProductModelLists;
  adminGuide: AdminGuideSettings;
  skinPhotos: SkinPhotoLibrary;
}

export const DEFAULT_ADMIN_GUIDE: AdminGuideSettings = {
  title: "Admin Handbook",
  intro:
    "Everything you need to run SkinVault: where each control lives, what it changes on the storefront, and the order to do things in.",
  supportNote:
    "Settings are saved per section — always press the Save button inside the card you edited. Storefront changes appear after the visitor's next page load.",
  previewRoutes: [
    { label: "Homepage", path: "/" },
    { label: "Catalog", path: "/catalog" },
    { label: "Skin Builder", path: "/configurator" },
    { label: "Tutorials", path: "/tutorial" },
    { label: "Shipping policy", path: "/shipping-policy" },
  ],
  sections: [
    {
      id: "getting-started",
      title: "Getting started",
      icon: "Rocket",
      summary: "How the dashboard is organised and what to check first every day.",
      steps: [
        "Overview: revenue, orders and product counts at a glance — your daily health check.",
        "Analytics: traffic, devices, countries and the Skin Builder material audit.",
        "Orders: fulfilment, tracking numbers, invoices.",
        "Products / Discounts: Shopify catalogue and promo codes.",
        "Settings: everything visible on the storefront (content, theme, SEO).",
      ],
      tips: "Start with Overview, then Orders. Anything cosmetic lives under Settings.",
    },
    {
      id: "orders",
      title: "Orders & fulfilment",
      icon: "ShoppingCart",
      summary: "Process an order from payment to delivered.",
      steps: [
        "Open the Orders tab and select the order you want to process.",
        "Use the printer icon to generate the branded PDF invoice for the parcel.",
        "Add the carrier tracking number — it syncs to Shopify and emails the customer automatically.",
        "The customer sees the tracking link in their profile immediately after saving.",
      ],
      tips: "If an order is missing, run a Shopify sync from Settings → Shopify.",
    },
    {
      id: "products",
      title: "Products & model lists",
      icon: "Package",
      summary: "Manage the catalogue and the model dropdowns shown on product pages.",
      steps: [
        "Products tab: edit titles, prices, images and visibility (synced with Shopify).",
        "Settings → Product Models: manage the device model dropdown groups per product family.",
        "Settings → Categories: control which categories and brands appear in the header and catalog.",
        "Settings → Configurator: add extra device models to the Skin Builder.",
      ],
      tips: "Each product family has its own model list — phones never appear on console products.",
    },
    {
      id: "design",
      title: "Design & theme",
      icon: "Palette",
      summary: "Change colours, fonts, radius and hero content without touching code.",
      steps: [
        "Settings → Theme: primary colour, accent colour, fonts, text size and corner radius, with a live preview.",
        "Settings → Hero / Homepage / Footer: headlines, stats, features, social links.",
        "Settings → Branding: site name, logo wording, tagline.",
        "Use the storefront preview on this page to confirm the result on desktop and mobile.",
      ],
      tips: "Preview first, save second. Presets give you a good starting point in one click.",
    },
    {
      id: "content",
      title: "Content & marketing",
      icon: "Megaphone",
      summary: "Announcements, testimonials, FAQ, tutorials and SEO.",
      steps: [
        "Settings → Announcements: rotating bar at the top of the site.",
        "Settings → Testimonials & FAQ: social proof and objection handling on the homepage.",
        "Settings → Tutorial: installation videos grouped per device type.",
        "Settings → SEO: page title, meta description, social share image, analytics ID.",
      ],
      tips: "Keep the page title under 60 characters and the meta description under 160.",
    },
    {
      id: "users",
      title: "Users & access",
      icon: "Users",
      summary: "Who can reach the dashboard and how activity is tracked.",
      steps: [
        "Users tab: view registered customers and grant or revoke the admin role.",
        "Activity tab: audit trail of every admin action with email and timestamp.",
        "Admin rights are stored server-side — never share your login.",
      ],
      tips: "Grant admin sparingly; moderators do not get destructive permissions.",
    },
  ],
};

const defaultSettings: SiteSettings = {
  branding: { siteName: "SkinVault", logoText1: "Skin", logoText2: "Vault", tagline: "Premium 3M Vinyl Skins" },
  testimonials: [],
  homepage: { whoAreWeText: "", announcementBar: "", announcementBarEnabled: false, quizEnabled: true },
  faq: [],
  categories: [],
  announcements: [],
  hero: {
    titleLine1: "Your Device.",
    titleLine2: "Your Style.",
    description: "Premium 3M vinyl skins that protect and transform your devices with zero bulk.",
    launchDate: "2026-06-01T00:00:00",
    showCountdown: true,
    ctaPrimaryText: "Browse Skins",
    ctaPrimaryLink: "/shop",
    ctaSecondaryText: "Skin Builder",
    ctaSecondaryLink: "/configurator",
    stats: [
      { value: "50K+", label: "Skins Sold" },
      { value: "3M", label: "Premium Vinyl" },
      { value: "4.9★", label: "Average Rating" },
      { value: "0.2mm", label: "Ultra Thin" },
    ],
    features: [
      { title: "Military-Grade Protection", description: "3M vinyl that shields against scratches, scuffs, and daily wear.", icon: "Shield" },
      { title: "Zero Bulk", description: "Ultra-thin skins that add zero thickness to your device.", icon: "Layers" },
      { title: "Bubble-Free Application", description: "Air-release technology for a flawless, professional finish.", icon: "Zap" },
      { title: "Premium Materials", description: "Only genuine 3M vinyl for unmatched durability and feel.", icon: "Star" },
    ],
    storyParagraphs: [],
  },
  heroSlides: DEFAULT_HERO_SLIDES,
  footer: {
    copyrightText: "SkinVault. All rights reserved.",
    socialLinks: [
      { platform: "Instagram", url: "", enabled: false },
      { platform: "TikTok", url: "", enabled: false },
      { platform: "X / Twitter", url: "", enabled: false },
      { platform: "Facebook", url: "", enabled: false },
      { platform: "YouTube", url: "", enabled: false },
    ],
    showNewsletter: true,
    newsletterTitle: "Stay Updated",
    newsletterDescription: "Get 10% off your first order.",
  },
  theme: {
    primaryHue: 33,
    primarySaturation: 100,
    primaryLightness: 55,
    accentHue: 33,
    accentSaturation: 100,
    accentLightness: 55,
    borderRadius: 0.75,
    fontFamily: "Inter",
    headingFont: "Inter",
    fontScale: 1,
  },
  seo: {
    pageTitle: "SkinVault — Premium 3M Vinyl Skins",
    metaDescription: "Transform your devices with premium 3M vinyl skins. Scratch-proof, bubble-free, ultra-thin protection for iPhones, MacBooks, cameras, and more.",
    ogImage: "",
    favicon: "",
    googleAnalyticsId: "",
  },
  shipping: {
    title: "Shipping Policy",
    sections: [],
  },
  configuratorExtras: { models: [] },
  productModelLists: DEFAULT_PRODUCT_MODEL_LISTS,
  adminGuide: DEFAULT_ADMIN_GUIDE,
  skinPhotos: DEFAULT_SKIN_PHOTOS,
  tutorial: {
    enabled: true,
    badge: "Step-by-step guidance",
    title1: "Application",
    title2: "Tutorials",
    intro: "Watch our installation videos to apply your skin like a pro.",
    helpTitle: "Need a hand?",
    helpDesc: "Our team is here to help you with any installation questions.",
    contactEmail: "support@skin-vault.fr",
    contactCtaText: "Contact support",
    watchCtaText: "Watch installation",
    tabs: [
      {
        id: "phone",
        label: "Phone",
        icon: "Smartphone",
        enabled: true,
        tutorials: [
          { id: "back-protection", label: "Back Protection", description: "Apply the protective film on the back of your phone.", icon: "ShieldCheck", youtubeId: "dQw4w9WgXcQ", duration: "4:32", difficulty: "Easy", preview: "" },
          { id: "back-skin", label: "Back Skin", description: "Install your decorative back skin perfectly.", icon: "Layers", youtubeId: "dQw4w9WgXcQ", duration: "5:45", difficulty: "Medium", preview: "" },
          { id: "screen-protection", label: "Screen Protection", description: "Apply your screen protector bubble-free.", icon: "Smartphone", youtubeId: "dQw4w9WgXcQ", duration: "6:12", difficulty: "Medium", preview: "" },
          { id: "all-around", label: "All-Around Skin", description: "Wrap your phone completely with full coverage.", icon: "Sparkles", youtubeId: "dQw4w9WgXcQ", duration: "8:00", difficulty: "Advanced", preview: "" },
        ],
      },
      {
        id: "tablet",
        label: "Tablet",
        icon: "Tablet",
        enabled: true,
        tutorials: [
          { id: "tablet-back", label: "Tablet Back Skin", description: "Install your tablet back skin step by step.", icon: "Layers", youtubeId: "dQw4w9WgXcQ", duration: "7:20", difficulty: "Medium", preview: "" },
          { id: "tablet-screen", label: "Tablet Screen Protection", description: "Apply your tablet screen protector flawlessly.", icon: "Tablet", youtubeId: "dQw4w9WgXcQ", duration: "5:30", difficulty: "Easy", preview: "" },
        ],
      },
      {
        id: "laptop",
        label: "Laptop",
        icon: "Laptop",
        enabled: true,
        tutorials: [
          { id: "laptop-skin", label: "Laptop Skin", description: "Install your laptop lid and palm rest skins.", icon: "Laptop", youtubeId: "dQw4w9WgXcQ", duration: "5:00", difficulty: "Easy", preview: "" },
        ],
      },
      {
        id: "console",
        label: "Console",
        icon: "Gamepad2",
        enabled: true,
        tutorials: [
          { id: "console-skin", label: "Console Skin", description: "Apply your console skin like a pro.", icon: "Gamepad2", youtubeId: "dQw4w9WgXcQ", duration: "5:15", difficulty: "Medium", preview: "" },
        ],
      },
      {
        id: "accessories",
        label: "Accessories",
        icon: "Headphones",
        enabled: true,
        tutorials: [
          { id: "accessories", label: "Accessories", description: "Skin your accessories with precision.", icon: "Headphones", youtubeId: "dQw4w9WgXcQ", duration: "2:30", difficulty: "Medium", preview: "" },
        ],
      },
    ],
  },
};

async function fetchSiteSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase
    .from("site_settings")
    .select("key, value");

  if (error) {
    console.error("Failed to fetch site settings:", error);
    return defaultSettings;
  }

  const settings = { ...defaultSettings };
  for (const row of data || []) {
    const key = row.key as keyof SiteSettings;
    if (key in settings) {
      // Deep merge defaults with saved values so new fields get defaults
      if (typeof settings[key] === "object" && !Array.isArray(settings[key])) {
        (settings as any)[key] = { ...(settings as any)[key], ...(row.value as any) };
      } else {
        (settings as any)[key] = row.value;
      }
    }
  }
  return settings;
}

export function useSiteSettings() {
  return useQuery({
    queryKey: ["site-settings"],
    queryFn: fetchSiteSettings,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
